<template>
  <div class="ncrm-privacy-registry">
    <div class="ncrm-privacy-registry__views mb-3">
      <NcrmSegmentedControl
        :model-value="state.view"
        :items="viewItems"
        :aria-label="$t(`${copyKey}.viewsLabel`)"
        @update:model-value="patch({ view: $event })"
      />
    </div>
    <NcrmPrivacyRegistryFilters class="mb-2" :state="state" :purposes="purposes" @patch="patch" />
    <div class="mb-4">
      <NcrmPrivacyPurposeLink v-if="hasPurposeFilter" :purpose-id="state.purposeId" />
    </div>

    <v-alert v-if="errorMessage" :type="forbidden ? 'warning' : 'error'">
      {{ errorMessage }}
      <template v-if="!forbidden" #append>
        <v-btn variant="text" @click="fetchRegistry">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <template v-else>
      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
        <div class="text-body-2 text-medium-emphasis">
          {{ $t(`${copyKey}.registry.total`, { count: total }) }}
          <span v-if="!canOpenRecords"> · {{ $t(`${copyKey}.registry.recordsReadOnly`) }}</span>
        </div>
        <v-btn v-if="canManageEvidence" size="small" variant="tonal" prepend-icon="mdi-file-delimited-outline"
          :loading="exporting" :disabled="loading || !total" @click="exportCsv">
          {{ $t(`${copyKey}.registry.export.action`) }}
        </v-btn>
      </div>
      <v-alert v-if="exportError" type="warning" density="compact" closable class="mb-2"
        @click:close="exportError = ''">{{ exportError }}</v-alert>
      <v-alert v-if="retryMessage" :type="retryMessage.type" density="compact" closable class="mb-2"
        role="status" @click:close="retryMessage = null">{{ retryMessage.text }}</v-alert>
      <v-data-table-server
        :headers="headers"
        :items="viewItemsOnly"
        :items-length="total"
        :loading="loading"
        :page="state.page"
        :items-per-page="pageSize"
        :items-per-page-options="[]"
        :no-data-text="$t(`${copyKey}.registry.${hasFilters ? 'emptyFiltered' : 'empty'}`)"
        :row-props="({ item }) => (isEvidenceView || recordOf(item) ? { style: 'cursor: pointer' } : {})"
        item-value="id"
        @update:page="patch({ page: $event }, { keepPage: true })"
        @click:row="(_, { item }) => openRow(item)"
      >
        <template #[`item.display_name`]="{ item }">
          <span class="font-weight-medium">{{ item.display_name || `#${item.id}` }}</span>
        </template>
        <template #[`item.summary_state`]="{ item }">
          <NcrmPrivacyStatusChip :state="item.summary_state" :total="item.contact_points_total" />
        </template>
        <template #[`item.last_event_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.last_event_at) }}</span>
        </template>
        <template #[`item.value_display`]="{ item }">
          <span class="font-weight-medium">{{ item.value_display }}</span>
          <div class="text-caption text-medium-emphasis">{{ label("record.channels", item.type) }}</div>
        </template>
        <template #[`item.subject`]="{ item }">
          <template v-if="item.subject">
            {{ item.subject.name || `#${item.subject.id}` }}
            <div class="text-caption text-medium-emphasis">
              {{ $t(`${copyKey}.registry.subjectTypes.${item.subject.type}`) }}
              <span v-if="item.active_links > 1"> · {{ $t(`${copyKey}.registry.activeLinks`, { count: item.active_links }) }}</span>
            </div>
          </template>
          <span v-else class="text-medium-emphasis">{{ $t(`${copyKey}.registry.noSubject`) }}</span>
        </template>
        <template #[`item.classification`]="{ item }">
          {{ item.classification ? label("record.contactPoints.classifications", item.classification) : "—" }}
        </template>
        <template #[`item.pairs`]="{ item }">
          <span v-if="!item.pairs.total" class="text-medium-emphasis">{{ $t(`${copyKey}.registry.noPairs`) }}</span>
          <span v-else class="text-no-wrap">
            {{ $t(`${copyKey}.registry.pairsSummary`, item.pairs) }}
          </span>
        </template>
        <template #[`item.preference_links`]="{ item }">
          <v-btn v-if="item.type === 'email'" size="small" variant="text" prepend-icon="mdi-link-variant"
            @click.stop="openLinks(item, $event)">{{ $t(`${copyKey}.registry.preferenceLinks`) }}</v-btn>
        </template>
        <!-- GDPR-123: managerul Privacy, inclusiv rolul `privacy-manager` fără acces la fișă, exportă aici datele persoanei. -->
        <template #[`item.dsar_export`]="{ item }">
          <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-download" :disabled="Boolean(dsarExportingId)"
            :loading="dsarExportingId === item.id" @click.stop="exportDsar(item)">{{ dsar("action") }}</v-btn>
        </template>
        <!-- S8.5b: managerul Privacy (fără acces la fișă) reia aici jobul eșuat al punctului. -->
        <template #[`item.mailjet_retry`]="{ item }">
          <v-btn v-if="item.mailjet_failed_job_id" size="small" variant="text" color="primary" :disabled="Boolean(retryingJob)"
            :loading="retryingJob === item.mailjet_failed_job_id" @click.stop="retrySync(item)">{{ sync("retry") }}</v-btn>
        </template>
        <template #[`item.recorded_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.recorded_at) }}</span>
        </template>
        <template #[`item.event_type`]="{ item }">{{ label("record.events", item.event_type) }}</template>
        <template #[`item.purpose`]="{ item }">
          {{ purposeLabel(item) }}
          <div v-if="item.channel_code" class="text-caption text-medium-emphasis">
            {{ label("record.channels", item.channel_code) }}
          </div>
        </template>
        <template #[`item.source_type`]="{ item }">{{ label("record.sources", item.source_type) }}</template>
        <template #[`item.event_subject`]="{ item }">
          {{ item.subject ? item.subject.name || `#${item.subject.id}` : "—" }}
          <div class="text-caption text-medium-emphasis">
            {{ item.contact_point ? item.contact_point.value_display : $t("ncrm.contacts.dataProtection.record.recordScope") }}
          </div>
        </template>
        <template #[`item.created_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.created_at) }}</span>
        </template>
        <template #[`item.evidence_type`]="{ item }">{{ $t(`${copyKey}.registry.evidenceTypes.${item.evidence_type}`) }}</template>
        <template #[`item.evidence_event`]="{ item }">
          {{ label("record.events", item.event.event_type) }}
          <div class="text-caption text-medium-emphasis">{{ formatDateTime(item.event.occurred_at) }}</div>
        </template>
        <template #[`item.evidence_subject`]="{ item }">
          {{ item.subject ? item.subject.name || `#${item.subject.id}` : "—" }}
          <div v-if="item.contact_point" class="text-caption text-medium-emphasis">{{ item.contact_point.value_display }}</div>
        </template>
        <template #[`item.attachment_available`]="{ item }">
          <v-icon v-if="item.attachment_available" size="18" :title="$t(`${copyKey}.registry.attachmentOptions.with`)">mdi-paperclip</v-icon>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
        <template #[`item.evidence_count`]="{ item }">
          <v-icon v-if="item.evidence_count" size="18" :title="$t(`${copyKey}.registry.hasEvidence`)">mdi-paperclip</v-icon>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
      </v-data-table-server>
    </template>
    <NcrmPrivacyPreferenceLinksDrawer v-model="linksOpen" :contact-point="linksPoint" :opener="linksOpener" />
    <NcrmPrivacyEvidenceDetailDrawer v-model="evidenceOpen" :evidence="selectedEvidence" :can-manage="canManageEvidence"
      @doi-sent="handleDoiSent" />
    <v-snackbar v-model="doiSent" :color="doiSentOutcome === 'accepted' ? 'success' : 'warning'" :timeout="5000">
      {{ $t(`ncrm.contacts.dataProtection.record.doiResend.sent.${doiSentOutcome}`) }}
    </v-snackbar>
    <v-snackbar v-model="dsarDone" color="success" :timeout="3500">{{ dsar("done") }}</v-snackbar>
    <v-snackbar v-model="exportDone" color="success" :timeout="3500">
      {{ $t(`${copyKey}.registry.export.done`, { count: exportedRows }) }}
    </v-snackbar>
  </div>
</template>

<script>
import NcrmPrivacyStatusChip from "@/components/ncrm/shared/NcrmPrivacyStatusChip.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import { hasPrivacyManagerRole, hasRecordAccess } from "@/Utile/ncrmPrivacyAccess.mjs";
import NcrmPrivacyEvidenceDetailDrawer from "./NcrmPrivacyEvidenceDetailDrawer.vue";
import NcrmPrivacyPreferenceLinksDrawer from "./NcrmPrivacyPreferenceLinksDrawer.vue";
import NcrmPrivacyPurposeLink from "./NcrmPrivacyPurposeLink.vue";
import NcrmPrivacyRegistryFilters from "./NcrmPrivacyRegistryFilters.vue";
import {
  downloadPersonDsar, exportRegistryCsv, fetchPrivacyPurposes, fetchPrivacyRegistry, retryMailjetSyncJob,
} from "./privacyWorkspaceApi.mjs";
import {
  REGISTRY_PAGE_SIZE,
  REGISTRY_VIEWS,
  SUBJECT_VIEWS,
  VIEW_FILTERS,
  buildWorkspaceQuery,
  hasActiveFilters,
  recordRoute,
  registryRequestParams,
} from "./privacyWorkspaceContracts.mjs";

const COLUMNS = Object.freeze({
  persons: ["display_name", "summary_state", "contact_points_total", "last_event_at"],
  companies: ["display_name", "summary_state", "contact_points_total", "last_event_at"],
  contact_points: ["value_display", "subject", "classification", "pairs"],
  events: ["recorded_at", "event_type", "purpose", "source_type", "event_subject", "evidence_count"],
  evidence: ["created_at", "evidence_type", "evidence_event", "purpose", "evidence_subject", "attachment_available"],
});

export default {
  name: "NcrmPrivacyRegistry",
  components: {
    NcrmPrivacyEvidenceDetailDrawer, NcrmPrivacyPreferenceLinksDrawer, NcrmPrivacyPurposeLink, NcrmPrivacyRegistryFilters, NcrmPrivacyStatusChip, NcrmSegmentedControl,
  },
  props: {
    state: { type: Object, required: true },
  },
  emits: ["update-state"],
  data: () => ({
    loading: false, errorMessage: "", forbidden: false, items: [], itemsView: null, total: 0,
    requestSequence: 0, canOpenRecords: false, purposes: [], purposesLoaded: false,
    canManageEvidence: false, evidenceOpen: false, selectedEvidence: null, linksOpen: false, linksPoint: null, linksOpener: null,
    exporting: false, exportError: "", exportDone: false, exportedRows: 0, doiSent: false, doiSentOutcome: "accepted",
    retryingJob: null, retryMessage: null, dsarExportingId: null, dsarDone: false,
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace"; },
    pageSize() { return REGISTRY_PAGE_SIZE; },
    // Cheia vine din URL, nu din parametrii API: perioada devine `from` abia la
    // request, iar `Date.now()` nu trebuie să declanșeze reîncărcări.
    requestKey() { return JSON.stringify(buildWorkspaceQuery({ ...this.state, section: "registry" })); },
    hasFilters() { return hasActiveFilters(this.state); },
    isEvidenceView() { return this.state.view === "evidence"; },
    hasPurposeFilter() { return VIEW_FILTERS[this.state.view].includes("purposeId"); },
    // Rândurile au forma vederii care le-a cerut. Imediat după schimbarea vederii,
    // coloanele noi nu trebuie randate peste rândurile vechi până sosește răspunsul.
    viewItemsOnly() { return this.itemsView === this.state.view ? this.items : []; },
    viewItems() {
      return REGISTRY_VIEWS.map((value) => ({ value, label: this.$t(`${this.copyKey}.views.${value}`) }));
    },
    headers() {
      // S7.3b-3: preference-link recovery is Privacy-manager only (the API refuses other roles too).
      const keys = this.state.view === "contact_points" && this.canManageEvidence
        ? [...COLUMNS.contact_points, "preference_links", ...(this.state.mailjetSync ? ["mailjet_retry"] : [])]
        : [...COLUMNS[this.state.view], ...(this.state.view === "persons" && this.canManageEvidence ? ["dsar_export"] : [])];
      return keys.map((key) => ({
        key,
        title: this.$t(`${this.copyKey}.registry.columns.${key}`),
        sortable: false,
      }));
    },
  },
  watch: {
    // Orice schimbare de URL (KPI, filtru, pagină, Back) reîncarcă automat.
    requestKey: { immediate: true, handler() { this.fetchRegistry(); } },
    "state.view": { immediate: true, handler(view) { if (VIEW_FILTERS[view].includes("purposeId")) this.loadPurposes(); } },
  },
  created() {
    this.canOpenRecords = hasRecordAccess();
    this.canManageEvidence = hasPrivacyManagerRole();
  },
  methods: {
    label(group, value) { return this.$t(`ncrm.contacts.dataProtection.${group}.${value}`); },
    // Același fallback ca timeline-ul din tabul de record: scop, apoi categorie.
    purposeLabel(item) {
      if (item.purpose) return item.purpose.name || `#${item.purpose.id}`;
      return item.purpose_category ? this.label("record.categories", item.purpose_category) : "—";
    },
    // Un filtru nou pornește de la prima pagină; numai paginarea păstrează pagina.
    patch(changes, { keepPage = false } = {}) {
      this.$emit("update-state", { ...this.state, ...changes, ...(keepPage ? {} : { page: 1 }) });
    },
    async fetchRegistry() {
      const sequence = ++this.requestSequence;
      this.loading = true; this.errorMessage = ""; this.forbidden = false;
      try {
        const data = await fetchPrivacyRegistry(registryRequestParams(this.state));
        if (sequence !== this.requestSequence) return;
        this.items = Array.isArray(data?.items) ? data.items : [];
        this.itemsView = this.state.view;
        this.total = Number(data?.total || 0);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        // Rezultatele filtrului anterior nu rămân pe ecran sub o eroare nouă.
        this.items = []; this.total = 0;
        this.forbidden = Number(error?.response?.status) === 403;
        this.errorMessage = this.$t(`${this.copyKey}.${this.forbidden ? "noPermission" : "loadError"}`);
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    // Scopurile se încarcă o singură dată; fără ele filtrul rămâne gol, nu blochează registrul.
    handleDoiSent(outcome) {
      this.doiSentOutcome = outcome === "uncertain" ? "uncertain" : "accepted";
      this.doiSent = true;
      this.fetchRegistry();
    },
    async loadPurposes() {
      if (this.purposesLoaded) return;
      this.purposesLoaded = true;
      try {
        this.purposes = await fetchPrivacyPurposes();
      } catch {
        this.purposes = [];
        this.purposesLoaded = false;
      }
    },
    recordOf(item) {
      if (!this.canOpenRecords) return null;
      if (SUBJECT_VIEWS.includes(this.state.view)) return recordRoute(this.state.view, item.id);
      return item.subject ? recordRoute(item.subject.type, item.subject.id) : null;
    },
    // Exportul (manager-only) trimite filtrele vederii curente, fără paginare;
    // rândurile le produce serverul. Peste limită, cere filtre mai restrânse.
    async exportCsv() {
      this.exporting = true; this.exportError = "";
      try {
        const params = { ...registryRequestParams(this.state) };
        delete params.limit;
        delete params.offset;
        const { blob, filename, rowCount } = await exportRegistryCsv(params);
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement("a"), { href: url, download: filename }).click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.exportedRows = rowCount;
        this.exportDone = true;
      } catch (error) {
        const code = error?.response?.data?.code;
        this.exportError = this.$t(`${this.copyKey}.registry.export.${code === "privacy_export_limit" ? "limit" : "error"}`);
      } finally {
        this.exporting = false;
      }
    },
    sync(key) { return this.$t(`ncrm.contacts.dataProtection.record.mailjetSync.${key}`); },
    dsar(key) { return this.$t(`ncrm.contacts.dataProtection.record.dsarExport.${key}`); },
    // GDPR-123: același export ca din fișa persoanei; eroarea apare în alerta de export a registrului.
    async exportDsar(item) {
      this.dsarExportingId = item.id; this.exportError = ""; this.dsarDone = false;
      try {
        await downloadPersonDsar(item.id);
        this.dsarDone = true;
      } catch (error) {
        this.exportError = this.dsar(Number(error?.response?.status) === 404 ? "notFound" : "error");
      } finally {
        this.dsarExportingId = null;
      }
    },
    // S8.5b: success reloads the filtered list (the point leaves it once the job is pending); an error stays visible.
    async retrySync(item) {
      this.retryingJob = item.mailjet_failed_job_id; this.retryMessage = null;
      try {
        await retryMailjetSyncJob(item.mailjet_failed_job_id);
        this.retryMessage = { type: "success", text: this.sync("retried") };
        await this.fetchRegistry();
      } catch {
        this.retryMessage = { type: "error", text: this.sync("retryError") };
      } finally {
        this.retryingJob = null;
      }
    },
    // Works without record access: the drawer needs only the registry's contact point.
    openLinks(item, event) {
      Object.assign(this, { linksPoint: { id: item.id, value: item.value_display }, linksOpener: event?.currentTarget || null,
        linksOpen: true });
    },
    openRecord(item) {
      const route = this.recordOf(item);
      if (route) this.$router.push(route);
    },
    // O dovadă se deschide în drawer, pentru orice rol din registru: managerul
    // primește detaliul auditat, ceilalți starea explicită „fără permisiune".
    openRow(item) {
      if (!this.isEvidenceView) return this.openRecord(item);
      this.selectedEvidence = item;
      this.evidenceOpen = true;
      return undefined;
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
/* Patru vederi nu încap pe un rând la 390px: controlul se rupe pe două rânduri,
   fără scroll orizontal. */
@media (max-width: 600px) {
  .ncrm-privacy-registry__views :deep(.ncrm-segmented) { flex-wrap: wrap; }
}
</style>
